import { isAxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import type { AuthenticatedRequest } from '@app/rest/rest.lib'

import {
  addOrUpdateMedicationRecordEntryOnPatientMedicationHistory,
  addMedicationRecordToPatientMedicationHistory,
  addMedicationToPatientMedicationHistory,
  ErrorType,
  getMedicationDosageUnits,
  getMedicationFrequencyUnits,
  getMedicationRoutes,
  getPatientMedicationHistory,
  getMedicationRecordEntryDefaults,
  ResponseStatus,
  updateMedicationRecordOnPatientMedicationHistory,
  updateMedicationOnPatientMedicationHistory,
  deleteMedicationOnPatientMedicationHistory,
  deleteMedicationRecordOnPatientMedicationHistory,
  isMedicationError,
} from './patient-medication-history.rest'

import type {
  IUseMedicationDosageUnits,
  IUseMedicationFrequencyUnits,
  IUseMedicationRoutes,
  IUsePatientMedicationHistory,
  IUseRecordEntryDefaultValues,
  MedicationDosageUnit,
  MedicationFrequencyUnit,
  MedicationRoute,
  PatientMedicationHistory,
  PatientMedicationId,
  PatientMedicationRecordEntryStatus,
  PatientMedicationRecordId,
  PatientMedicationRecordEntryDefault,
  PatientMedicationDosage,
  PatientMedicationFrequency,
  PatientMedicationFrequencyUnitId,
  PatientMedicationIndications,
  PatientMedicationPrescriber,
  PatientMedicationComments,
  PatientMedicationRecommendations,
} from './patient-medication-history.interface'
import type {
  IPatientMedicationHistoryResponse,
  ValidationError} from './patient-medication-history.rest';
import type { IPatientId } from '../patient/patient.interface'



export function usePatientMedicationHistory(
  patientId: IPatientId,
): IUsePatientMedicationHistory {
  const requestor = useAuthenticatedRequestor()

  const [patientMedicationHistory, setPatientMedicationHistory] =
    useState<PatientMedicationHistory>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorType, setErrorType] = useState<ErrorType>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )

  const clearError = useCallback(() => {
    setIsError(false)
    setErrorType(null)
    setValidationErrors([])
  }, [])

  const handleServerRequest = useCallback(
    async (
      request: AuthenticatedRequest<IPatientMedicationHistoryResponse>,
    ) => {
      clearError()
      setIsLoading(true)
      try {
        const response = await requestor(request)
        if (
          response.status === 200 &&
          response.data.status === ResponseStatus.Success
        ) {
          setPatientMedicationHistory(response.data.patientMedicationHistory)
        }
        setIsLoading(false)
      } catch (e) {
        if (isAxiosError(e)) {
          const responseData = e.response.data
          setIsError(true)
          if (isMedicationError(responseData)) {
            setErrorType(responseData.errorType)
            if (responseData.errorType === ErrorType.ValidationError) {
              setValidationErrors(responseData.validationErrors)
            }
          } else {
            throw new Error(
              'Could not add medication record. Please try again.',
            )
          }
        } else {
          setIsError(true)
          throw new Error('Could not add medication record. Please try again.')
        }
      }
    },

    [clearError, requestor],
  )

  const fetchPatientMedicationHistory = useCallback(async () => {
    handleServerRequest(getPatientMedicationHistory(patientId))
  }, [handleServerRequest, patientId])

  const addMedication = useCallback(
    async (
      medicationDosageUnitId: string,
      medicationRouteId: string,
      name: string,
    ) => {
      handleServerRequest(
        addMedicationToPatientMedicationHistory(
          patientId,
          medicationDosageUnitId,
          medicationRouteId,
          name,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  const deleteMedication = useCallback(
    async (patientMedicationId: PatientMedicationId) => {
      handleServerRequest(
        deleteMedicationOnPatientMedicationHistory(
          patientId,
          patientMedicationId,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  const updateMedication = useCallback(
    async (
      patientMedicationId: PatientMedicationId,
      medicationDosageUnitId: string,
      medicationRouteId: string,
      name: string,
    ) => {
      handleServerRequest(
        updateMedicationOnPatientMedicationHistory(
          patientId,
          patientMedicationId,
          medicationDosageUnitId,
          medicationRouteId,
          name,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  const addMedicationRecord = useCallback(
    async (activeDate: string) => {
      handleServerRequest(
        addMedicationRecordToPatientMedicationHistory(patientId, activeDate),
      )
    },
    [handleServerRequest, patientId],
  )

  const deleteMedicationRecord = useCallback(
    async (patientMedicationRecordId: PatientMedicationRecordId) => {
      handleServerRequest(
        deleteMedicationRecordOnPatientMedicationHistory(
          patientId,
          patientMedicationRecordId,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  const updateMedicationRecord = useCallback(
    async (
      patientMedicationRecordId: PatientMedicationRecordId,
      activeDate: string,
    ) => {
      handleServerRequest(
        updateMedicationRecordOnPatientMedicationHistory(
          patientId,
          patientMedicationRecordId,
          activeDate,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  const addOrUpdateMedicationRecordEntry = useCallback(
    async (
      patientMedicationId: PatientMedicationId,
      patientMedicationRecordId: PatientMedicationRecordId,
      status: PatientMedicationRecordEntryStatus,
      dosage: PatientMedicationDosage,
      frequency: PatientMedicationFrequency,
      frequencyUnitId: PatientMedicationFrequencyUnitId,
      indications?: PatientMedicationIndications,
      prescriber?: PatientMedicationPrescriber,
      comments?: PatientMedicationComments,
      recommendations?: PatientMedicationRecommendations,
    ) => {
      handleServerRequest(
        addOrUpdateMedicationRecordEntryOnPatientMedicationHistory(
          patientId,
          patientMedicationId,
          patientMedicationRecordId,
          status,
          dosage,
          frequency,
          frequencyUnitId,
          indications,
          prescriber,
          comments,
          recommendations,
        ),
      )
    },
    [handleServerRequest, patientId],
  )

  useEffect(() => {
    fetchPatientMedicationHistory()
  }, [fetchPatientMedicationHistory])

  return {
    addMedication,
    addMedicationRecord,
    addOrUpdateMedicationRecordEntry,
    deleteMedication,
    deleteMedicationRecord,
    errorType,
    isError,
    isLoading,
    patientMedicationHistory,
    updateMedication,
    updateMedicationRecord,
    validationErrors,
  }
}

export function useMedicationDosageUnits(): IUseMedicationDosageUnits {
  const requestor = useAuthenticatedRequestor()

  const [medicationDosageUnits, setMedicationDosageUnits] = useState<
    MedicationDosageUnit[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchMedicationDosageUnits = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(getMedicationDosageUnits())

    if (response.status === 200) {
      setMedicationDosageUnits(response.data.medicationDosageUnits)
    } else {
      setIsError(true)
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
    setIsLoading(false)
  }, [requestor])

  useEffect(() => {
    fetchMedicationDosageUnits()
  }, [fetchMedicationDosageUnits])

  return {
    medicationDosageUnits,
    isLoading,
    isError,
  }
}

export function useMedicationFrequencyUnits(): IUseMedicationFrequencyUnits {
  const requestor = useAuthenticatedRequestor()

  const [medicationFrequencyUnits, setMedicationFrequencyUnits] = useState<
    MedicationFrequencyUnit[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchMedicationFrequencyUnits = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(getMedicationFrequencyUnits())

    if (response.status === 200) {
      setMedicationFrequencyUnits(response.data.medicationFrequencyUnits)
    } else {
      setIsError(true)
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
    setIsLoading(false)
  }, [requestor])

  useEffect(() => {
    fetchMedicationFrequencyUnits()
  }, [fetchMedicationFrequencyUnits])

  return {
    medicationFrequencyUnits,
    isLoading,
    isError,
  }
}

export function useMedicationRoutes(): IUseMedicationRoutes {
  const requestor = useAuthenticatedRequestor()

  const [medicationRoutes, setMedicationRoutes] = useState<MedicationRoute[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchMedicationRoutes = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(getMedicationRoutes())

    if (response.status === 200) {
      setMedicationRoutes(response.data.medicationRoutes)
    } else {
      setIsError(true)
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
    setIsLoading(false)
  }, [requestor])

  useEffect(() => {
    fetchMedicationRoutes()
  }, [fetchMedicationRoutes])

  return {
    medicationRoutes,
    isLoading,
    isError,
  }
}

export function usePatientMedicationRecordEntryDefaultValues(): IUseRecordEntryDefaultValues {
  const requestor = useAuthenticatedRequestor()

  const [recordEntryDefaults, setRecordEntryDefaults] =
    useState<PatientMedicationRecordEntryDefault>()

  const sendRequestToServer = useMemo(() => {
    return async (
      patientId: string,
      patientMedicationId: PatientMedicationId,
      patientMedicationRecordId: PatientMedicationRecordId,
    ) => {
      const response = await requestor(
        getMedicationRecordEntryDefaults(
          patientId,
          patientMedicationId,
          patientMedicationRecordId,
        ),
      )
      if (response.status === 200) {
        setRecordEntryDefaults(response.data.defaults)
      } else {
        throw new Error(
          `Could not get record entry default values: '${response.status}'`,
        )
      }
    }
  }, [requestor])

  return {
    recordEntryDefaults,
    getRecordEntryDefaults: sendRequestToServer,
  }
}
